import { useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { usePlanStore } from '@/stores/planStore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { planQueries } from '@/services/plans/queries';
import { planMutations } from '@/services/plans/mutations';
import { PlanTable } from './PlanTable';
import { PlanForm } from './PlanForm';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ChannelSelection } from './ChannelSelection';
import { Plan } from '../types';
import type { VersionAwareProps } from '@/features/versions/types/props';

export function PlanTab(props: VersionAwareProps) {
  const queryClient = useQueryClient();
  const {
    isCreateModalOpen,
    isDeleteModalOpen,
    selectedPlan,
    openCreateModal: originalOpenCreateModal,
    closeCreateModal,
    openDeleteModal: originalOpenDeleteModal,
    closeDeleteModal,
    setSelectedPlan,
  } = usePlanStore();

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['plans', Number(props.providerId), Number(props.versionId)],
    queryFn: async () => {
      const plans = await planQueries.getPlans(
        Number(props.providerId), 
        Number(props.versionId)
      );
      
      // Get total channel count for the version
      const totalChannels = await planQueries.getTotalChannels(
        Number(props.providerId), 
        Number(props.versionId)
      );
      
      return plans.map(plan => ({
        ...plan,
        total_channels: totalChannels
      }));
    },
    enabled: Boolean(props.providerId && props.versionId),
  });

  const deleteMutation = useMutation({
    mutationFn: planMutations.deletePlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans', Number(props.providerId), Number(props.versionId)] });
      toast.success('The plan has been deleted successfully.');
      closeDeleteModal();
      setSelectedPlan(null);
    },
    onError: (error: Error) => {
      toast.error(error instanceof Error ? error.message : 'An unexpected error occurred');
    },
  });

  // Wrap actions with read-only checks
  const handleAction = useCallback((action: () => void) => {
    if (props.isReadOnly) {
      toast.error("This is a published version. Create a new version to make changes.");
      return;
    }
    action();
  }, [props.isReadOnly]);

  const handleEdit = useCallback((plan: Plan) => {
    handleAction(() => {
      setSelectedPlan(plan);
      originalOpenCreateModal();
    });
  }, [setSelectedPlan, originalOpenCreateModal, handleAction]);
  
  const handleDelete = useCallback((plan: Plan) => {
    handleAction(() => {
      setSelectedPlan(plan);
      originalOpenDeleteModal();
    });
  }, [setSelectedPlan, originalOpenDeleteModal, handleAction]);

  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Plans</h2>
        <Button 
          onClick={() => handleAction(originalOpenCreateModal)}
          disabled={props.isReadOnly}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Plan
        </Button>
      </div>

      <PlanTable
        plans={plans}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isReadOnly={props.isReadOnly}
      />

      {!props.isReadOnly && (
        <>
          <Dialog open={isCreateModalOpen} onOpenChange={closeCreateModal}>
            <DialogContent className="max-w-2xl" aria-describedby="plan-form-description">
              <DialogHeader>
                <DialogTitle>
                  {selectedPlan ? 'Edit Plan' : 'Create Plan'}
                </DialogTitle>
                <DialogDescription id="plan-form-description">
                  {selectedPlan ? 'Edit the plan details below.' : 'Fill in the details to create a new plan.'}
                </DialogDescription>
              </DialogHeader>
              <PlanForm
                providerId={Number(props.providerId)}
                versionId={Number(props.versionId)}
                plan={selectedPlan ?? undefined}
                onSuccess={() => {
                  closeCreateModal();
                  setSelectedPlan(null);
                }}
              />
            </DialogContent>
          </Dialog>

          <AlertDialog open={isDeleteModalOpen} onOpenChange={closeDeleteModal}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the plan
                  and remove its data from the server.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => selectedPlan && deleteMutation.mutate(selectedPlan.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Dialog open={isChannelModalOpen} onOpenChange={setIsChannelModalOpen}>
            <DialogContent className="max-w-4xl" aria-describedby="channel-assignment-description">
              <DialogHeader>
                <DialogTitle>
                  Assign Channels to {selectedPlan?.name}
                </DialogTitle>
                <DialogDescription id="channel-assignment-description">
                  Select channels to assign to this plan.
                </DialogDescription>
              </DialogHeader>
              {selectedPlan && (
                <ChannelSelection
                  plan={selectedPlan}
                  versionId={Number(props.versionId)}
                  onClose={() => {
                    setIsChannelModalOpen(false);
                    setSelectedPlan(null);
                  }}
                />
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
} 